import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileCheck,
  FilePen,
  FileText,
  Layers,
  Users,
  Plus,
  Send,
  X,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { FileUpload } from '@/components/ui/FileUpload';
import { Input } from '@/components/ui/Input';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { downloadPrivateFile } from '@/lib/download';
import { cn } from '@/lib/utils';
import type { HomeworkItem, HomeworkList, HomeworkPublication } from '@/types/homework';
import { homeworkApi } from '../api/homework.api';
import { teacherApi } from '../api/teacher.api';
import type { TaughtClass } from '../types/teacher';
import { formatInstituteDate, homeworkState, instituteToday } from '../utils';
import { RowsPerPageSelect } from '@/components/ui/RowsPerPageSelect';
import { HomeworkTable } from './homework/HomeworkTable';

const COPY = {
  school: {
    heading: 'Homework & Daily Practice',
    openForm: 'Post Homework',
    formTitle: '🏫 Post Homework',
    formHint: 'Daily reading and notebook practice, checked in class.',
    submit: 'Post Homework',
    loadError: 'Could not load this homework',
    saveError: 'Could not set this homework',
    editTitle: 'Edit Homework',
    editHint: 'Correct the details. Students see the change straight away.',
    editMenu: 'Edit Homework',
    updateError: 'Could not update this homework',
    publishError: 'Could not publish this homework',
    withdrawError: 'Could not withdraw this homework',
    emptyTitle: 'No homework set yet',
    emptyDescription:
      'Set the first piece of work for this class. It appears on their panel as soon as you do.',
    removeSubject: 'this homework',
    removeConfirm: 'Suspend homework',
    allTab: 'All Homework',
    overview: 'Homework Overview',
    quickCreate: 'Create Homework',
    quickCreateHint: 'Post new homework',
    countNoun: 'homework',
  },
  college: {
    heading: 'Course Assignments & Projects',
    openForm: 'Create Assignment',
    formTitle: '🎓 Create Course Assignment',
    formHint: 'Term project and coursework, submitted as a file and graded.',
    submit: 'Publish Assignment',
    loadError: 'Could not load these assignments',
    saveError: 'Could not set this assignment',
    editTitle: 'Edit Assignment',
    editHint: 'Correct the details. Students see the change straight away.',
    editMenu: 'Edit Assignment',
    updateError: 'Could not update this assignment',
    publishError: 'Could not publish this assignment',
    withdrawError: 'Could not withdraw this assignment',
    emptyTitle: 'No assignments set yet',
    emptyDescription:
      'Set the first assignment for this class. It appears on their panel as soon as you do.',
    removeSubject: 'this assignment',
    removeConfirm: 'Suspend assignment',
    allTab: 'All Assignments',
    overview: 'Assignments Overview',
    quickCreate: 'Create Assignment',
    quickCreateHint: 'Set a new assignment',
    countNoun: 'assignments',
  },
} as const;

interface TeacherHomeworkSectionProps {
  currentClass: TaughtClass;
  isFormOpen?: boolean;
  setIsFormOpen?: (open: boolean | ((prev: boolean) => boolean)) => void;
  searchQuery?: string;
}

/**
 * The list's filter, which is `homeworkState` plus "everything".
 *
 * "Drafts" is work saved but not yet published — only its author sees it.
 *
 * "Deleted" is not a state of live work but a separate read: withdrawn work is
 * soft-deleted, so the server lists it on its own when asked.
 */
type TabKey = 'all' | 'open' | 'completed' | 'draft' | 'deleted';

const TAB_KEYS: readonly TabKey[] = ['all', 'open', 'completed', 'draft', 'deleted'];

/** How many rows a page of the list holds, and what else can be chosen. */
const PAGE_SIZES = [5, 10, 20] as const;

/**
 * The list's filters, as they sit in the address bar, so reviewing a piece of
 * work and coming back finds the same tab, day and page.
 *
 * `view` is the tab strip — not `tab`, which the dashboard around this section
 * reads as which of its own sections is open. The search box belongs to the
 * dashboard's header, so it is kept there.
 */
type Filters = {
  view: TabKey;
  date: string;
  page: number;
  limit: number;
};

const DEFAULT_FILTERS: Filters = {
  view: 'all',
  date: '',
  page: 1,
  limit: 10,
};

const FILTER_OPTIONS = {
  allowed: {
    view: TAB_KEYS,
    limit: PAGE_SIZES,
  },
};

export function TeacherHomeworkSection({
  currentClass,
  isFormOpen: controlledIsFormOpen,
  setIsFormOpen: controlledSetIsFormOpen,
  searchQuery = '',
}: TeacherHomeworkSectionProps) {
  const navigate = useNavigate();

  const institutionType = useInstituteBranding((state) => state.branding?.institutionType);
  const isCollege = institutionType === 'COLLEGE';
  const copy = isCollege ? COPY.college : COPY.school;

  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { view: activeTab, date: onDate, page: requestedPage, limit: pageSize } = filters;

  // Any change to what is listed starts the reader on page 1 again — done
  // where the change is made, so arriving does not throw a restored page away.
  function updateFilters(patch: Partial<Filters>) {
    setFilters({ ...patch, page: patch.page ?? 1 });
  }

  const [internalFormOpen, setInternalFormOpen] = useState(false);
  const isFormOpen = controlledIsFormOpen !== undefined ? controlledIsFormOpen : internalFormOpen;
  const setIsFormOpen = controlledSetIsFormOpen ?? setInternalFormOpen;
  const [isSaving, setIsSaving] = useState(false);
  /** Which of the form's two save buttons is working, so only that one spins. */
  const [savingAs, setSavingAs] = useState<HomeworkPublication | null>(null);
  const [removing, setRemoving] = useState<HomeworkItem | null>(null);
  /** The work being corrected, when the form is open in edit mode rather than to post. */
  const [editing, setEditing] = useState<HomeworkItem | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  /**
   * The calendar date filter is cleared whenever the teacher switches class —
   * but not when the first class arrives, which is the screen loading, not a
   * switch, and would throw away a date and page restored from the address.
   */
  const classKey = `${currentClass.id}:${currentClass.classSectionUuid}`;
  const shownClassKey = useRef(currentClass.id ? classKey : '');
  useEffect(() => {
    if (!currentClass.id) return;
    const previous = shownClassKey.current;
    shownClassKey.current = classKey;
    if (previous && previous !== classKey) setFilters({ date: '', page: 1 });
  }, [classKey, currentClass.id, setFilters]);


  const [form, setForm] = useState({ title: '', dueDate: '', instructions: '', maxMarks: '' });
  const [attachment, setAttachment] = useState<File | null>(null);
  /** The edit's stored worksheet was taken off; sent with the save, not before. */
  const [removesSaved, setRemovesSaved] = useState(false);

  const [schoolSubmissionType, setSchoolSubmissionType] = useState<'notebook' | 'app'>('notebook');
  const [collegeSubmissionType, setCollegeSubmissionType] = useState<'pdf' | 'code' | 'zip'>('pdf');

  const sectionUuid = currentClass.classSectionUuid;

  const { data, isLoading, error, refetch } = useApiQuery<HomeworkList | null>(
    () =>
      sectionUuid
        ? homeworkApi.list(sectionUuid, onDate || undefined)
        : Promise.resolve(null),
    [sectionUuid, onDate],
  );

  // Fetched only while its tab is open — most visits never look at it.
  const isDeletedTab = activeTab === 'deleted';
  const withdrawn = useApiQuery<HomeworkList | null>(
    () =>
      sectionUuid && isDeletedTab
        ? homeworkApi.list(sectionUuid, onDate || undefined, 'withdrawn')
        : Promise.resolve(null),
    [sectionUuid, onDate, isDeletedTab],
  );

  const { data: register } = useApiQuery(
    () => (sectionUuid ? teacherApi.register(sectionUuid) : Promise.resolve(null)),
    [sectionUuid],
  );
  // The register's date is the server's; until it arrives, the institute's own
  // date stands in — never the device's UTC one, a day behind before 05:30 IST.
  const today = register?.date ?? instituteToday();

  const homework = data?.homework ?? [];
  /** What the list shows: live work, or what was withdrawn on the Deleted tab. */
  const listed = isDeletedTab ? (withdrawn.data?.homework ?? []) : homework;
  const listLoading = isDeletedTab ? withdrawn.isLoading : isLoading;
  const listError = isDeletedTab ? withdrawn.error : error;
  const listRefetch = isDeletedTab ? withdrawn.refetch : refetch;

  function resetForm() {
    setForm({ title: '', dueDate: '', instructions: '', maxMarks: '' });
    setAttachment(null);
    setRemovesSaved(false);
    setCollegeSubmissionType('pdf');
    setEditing(null);
  }

  /** Opens the one form, filled with the work as it stands. */
  function startEditing(item: HomeworkItem) {
    setForm({
      title: item.title,
      dueDate: item.dueDate,
      instructions: item.instructions,
      maxMarks: item.maxMarks === null ? '' : String(item.maxMarks),
    });
    setAttachment(null);
    setRemovesSaved(false);
    // PDF and ZIP are both a file to the server; the form can only say which
    // of the two the teacher meant by asking again, so it opens on PDF.
    setCollegeSubmissionType(item.submissionFormat === 'REPO_URL' ? 'code' : 'pdf');
    setEditing(item);
    setIsFormOpen(true);
  }

  // The form sits above the list; scroll to the top so the heading and form appear.
  useEffect(() => {
    if (isFormOpen || editing) {
      const timer = setTimeout(() => {
        const headerEl = document.getElementById('homework-page-header');
        if (headerEl) {
          headerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [isFormOpen, editing]);

  // The page's own "Post Homework" button can close the form from outside; an
  // edit left behind would otherwise reopen in place of a blank post.
  useEffect(() => {
    if (isFormOpen || !editing) return;
    setForm({ title: '', dueDate: '', instructions: '', maxMarks: '' });
    setAttachment(null);
    setRemovesSaved(false);
    setCollegeSubmissionType('pdf');
    setEditing(null);
  }, [isFormOpen, editing]);

  async function openWorksheet(item: HomeworkItem) {
    if (!item.attachment) return;
    try {
      await downloadPrivateFile(homeworkApi.worksheetPath(item.uuid), item.attachment.fileName);
    } catch {
      toast.error('Could not open the worksheet', 'Please try again.');
    }
  }

  async function handlePublish(event: FormEvent) {
    event.preventDefault();
    // Two submit buttons share the form so the browser's required-field checks
    // guard both; the one pressed says whether this is a draft.
    const submitter = event.nativeEvent instanceof SubmitEvent ? event.nativeEvent.submitter : null;
    const publication: HomeworkPublication =
      submitter instanceof HTMLButtonElement && submitter.value === 'DRAFT' ? 'DRAFT' : 'PUBLISHED';

    if (editing) {
      await handleUpdate(editing, publication);
      return;
    }
    setIsSaving(true);
    setSavingAs(publication);

    try {
      const created = await homeworkApi.create(
        {
          classSectionUuid: sectionUuid,
          subjectUuid: currentClass.subjectUuid,
          title: form.title.trim(),
          instructions: form.instructions.trim(),
          dueDate: form.dueDate,
          ...(form.maxMarks.trim() && { maxMarks: Number(form.maxMarks) }),
          ...(isCollege &&
            collegeSubmissionType === 'code' && { submissionFormat: 'REPO_URL' as const }),
          publication,
        },
        attachment,
      );

      toast.success(
        publication === 'DRAFT' ? `“${created.title}” is saved as a draft` : `“${created.title}” is set`,
        publication === 'DRAFT'
          ? 'Only you can see it. Publish it when it is ready.'
          : `${created.displayName} · ${created.subject.name}`,
      );
      resetForm();
      setIsFormOpen(false);
      refetch();
    } catch (caught) {
      toast.error(
        copy.saveError,
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
      setSavingAs(null);
    }
  }

  /**
   * Saves the form over existing work. `PUBLISHED` on a draft releases it; on
   * work already published it is simply what that work still is.
   */
  async function handleUpdate(item: HomeworkItem, publication: HomeworkPublication) {
    setIsSaving(true);
    setSavingAs(publication);
    const isPublishing = item.publication === 'DRAFT' && publication === 'PUBLISHED';

    try {
      const updated = await homeworkApi.update(
        item.uuid,
        {
          title: form.title.trim(),
          instructions: form.instructions.trim(),
          dueDate: form.dueDate,
          // Only the college form shows marks and the hand-in format; the school
          // form leaves both as they were set.
          ...(isCollege && {
            maxMarks: form.maxMarks.trim() ? Number(form.maxMarks) : null,
            submissionFormat: collegeSubmissionType === 'code' ? 'REPO_URL' : 'FILE',
          }),
          ...(isPublishing && { publication: 'PUBLISHED' as const }),
          // A new file replaces the old one on its own; removal is only for none.
          ...(removesSaved && !attachment && { removeAttachment: true as const }),
        },
        attachment,
      );

      toast.success(
        isPublishing ? `“${updated.title}” is set` : `“${updated.title}” is updated`,
        `${updated.displayName} · ${updated.subject.name}`,
      );
      resetForm();
      setIsFormOpen(false);
      refetch();
    } catch (caught) {
      const isPastDue = caught instanceof ApiRequestError && caught.code === 'HOMEWORK_PAST_DUE';
      toast.error(
        copy.updateError,
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
      // The day turned while the form was open: the list is stale, and the
      // option should leave the menu rather than fail again.
      if (isPastDue) {
        resetForm();
        setIsFormOpen(false);
        refetch();
      }
    } finally {
      setIsSaving(false);
      setSavingAs(null);
    }
  }

  /** Releases a draft as it stands, from the row's menu. */
  async function publishDraft(item: HomeworkItem) {
    // The server would refuse it; open the form on the date instead, which is
    // the one thing that has to change.
    if (item.dueDate < today) {
      toast.error('The due date has passed', 'Choose a new due date, then publish.');
      startEditing(item);
      return;
    }

    try {
      const published = await homeworkApi.update(item.uuid, { publication: 'PUBLISHED' });
      toast.success(`“${published.title}” is set`, `${published.displayName} · ${published.subject.name}`);
      refetch();
    } catch (caught) {
      toast.error(
        copy.publishError,
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    setIsRemoving(true);

    try {
      await homeworkApi.remove(removing.uuid);
      toast.success(`“${removing.title}” has been withdrawn`, 'Marks already given are kept.');
      setRemoving(null);
      refetch();
    } catch (caught) {
      // The day turned while the menu was open: close the dialog and reload, so
      // the option leaves the menu rather than failing again.
      if (caught instanceof ApiRequestError && caught.code === 'HOMEWORK_PAST_DUE') {
        setRemoving(null);
        refetch();
      }
      toast.error(
        copy.withdrawError,
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsRemoving(false);
    }
  }

  // Filter homework by tab, search, date
  const filteredHomework = useMemo(() => {
    let result = listed;

    // Filter by tab. The same rule the status pill and the overview tiles read,
    // so a count and the list it opens can never disagree.
    if (activeTab !== 'all' && activeTab !== 'deleted') {
      result = result.filter((item) => homeworkState(item) === activeTab);
    }

    // Filter by search query
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (h) =>
          h.title.toLowerCase().includes(q) ||
          h.instructions.toLowerCase().includes(q) ||
          h.subject.name.toLowerCase().includes(q),
      );
    }

    return result;
  }, [listed, activeTab, searchQuery]);

  // Pagination
  const totalPages = Math.max(Math.ceil(filteredHomework.length / pageSize), 1);
  // Capped, so a page restored from the address past the end of a list that has
  // since shrunk shows the last page rather than an empty table.
  const page = Math.min(requestedPage, totalPages);
  const paginatedHomework = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredHomework.slice(start, start + pageSize);
  }, [filteredHomework, page, pageSize]);

  /**
   * The overview tiles.
   *
   * The two states are counted rather than derived from one another, so they
   * always add up to the total and each tile is a set the reader can open by
   * clicking the tab of the same name. Drafts stay out of every tile, the total
   * included: the class has not been given them.
   */
  const overviewStats = useMemo(() => {
    const counts = { total: 0, open: 0, completed: 0, draft: 0 };
    for (const item of homework) {
      const state = homeworkState(item);
      counts[state] += 1;
      if (state !== 'draft') counts.total += 1;
    }

    return counts;
  }, [homework]);

  /**
   * The published piece set most recently, for "View Submissions".
   *
   * The list comes back by due date, so its first row may be a draft — which
   * nobody has been given — or older work due later. Ties on the day keep the
   * server's order.
   */
  const latestPublished = useMemo(
    () =>
      homework
        .filter((item) => item.publication === 'PUBLISHED')
        .reduce<HomeworkItem | null>(
          (latest, item) => (!latest || item.assignedDate > latest.assignedDate ? item : latest),
          null,
        ),
    [homework],
  );

  // Both forms ask it. Left empty, the work is unscored and the review screen
  // has no marks box — and once the due date passes it can no longer be added.
  const maxMarksField = (
    <Input
      label="Marks (Optional)"
      type="number"
      min={1}
      max={1000}
      placeholder="e.g. 50"
      hint={`Leave empty if this ${isCollege ? 'assignment' : 'homework'} is not scored.`}
      value={form.maxMarks}
      onChange={(event) => setForm({ ...form, maxMarks: event.target.value })}
    />
  );

  return (
    <div className="flex flex-col gap-6">
      {/* ================= POST / CREATE HOMEWORK FORM ================= */}
      {isFormOpen && (
        <form
          id="post-homework-form"
          ref={formRef}
          onSubmit={handlePublish}
          aria-label={editing ? copy.editTitle : copy.formTitle}
          className="card flex flex-col gap-5 p-6 border-2 border-indigo-500/30 rounded-[18px] bg-surface shadow-md animate-in fade-in duration-200"
        >
          {/* Top Banner */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line pb-4">
            <div>
              <h3 className="text-lg font-bold text-ink tracking-tight">
                {editing ? copy.editTitle : copy.formTitle}
              </h3>
              <p className="text-xs text-ink-3 font-medium mt-0.5">
                {editing ? copy.editHint : copy.formHint}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <span
                className={`px-3 py-1 rounded-pill text-xs font-bold border ${
                  isCollege
                    ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/25'
                    : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
                }`}
              >
                {currentClass.className} · {currentClass.subject}
              </span>
            </div>
          </div>

          {/* Form Fields */}
          {!isCollege ? (
            /* ================= SCHOOL HOMEWORK FORM ================= */
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Homework Title / Topic"
                  placeholder="e.g. Chapter 4 Textbook Practice"
                  required
                  maxLength={150}
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                />

                <DatePicker
                  label="Completion Due Date"
                  required
                  min={today}
                  value={form.dueDate}
                  onChange={(event) => setForm({ ...form, dueDate: event.target.value })}
                />
              </div>

              <Textarea
                label="Detailed Instructions for Students"
                rows={3}
                required
                maxLength={2000}
                placeholder="Complete the exercises in textbook. Write all answers in notebook..."
                value={form.instructions}
                onChange={(event) => setForm({ ...form, instructions: event.target.value })}
              />

              {maxMarksField}

              {/* School Submission Method Selector */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-ink-2">Submission Method in School</label>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setSchoolSubmissionType('notebook')}
                    className={`flex items-center gap-2.5 rounded-xl border p-3 text-left transition-all ${
                      schoolSubmissionType === 'notebook'
                        ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300 font-bold'
                        : 'border-line bg-surface hover:bg-surface-2 text-ink-2'
                    }`}
                  >
                    <span className="text-lg">📔</span>
                    <div>
                      <div className="text-xs font-bold">Bring Physical Notebook to Class</div>
                      <div className="text-2xs opacity-80">Checked by teacher in classroom</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSchoolSubmissionType('app')}
                    className={`flex items-center gap-2.5 rounded-xl border p-3 text-left transition-all ${
                      schoolSubmissionType === 'app'
                        ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300 font-bold'
                        : 'border-line bg-surface hover:bg-surface-2 text-ink-2'
                    }`}
                  >
                    <span className="text-lg">📱</span>
                    <div>
                      <div className="text-xs font-bold">Upload Photo in Student Portal</div>
                      <div className="text-2xs opacity-80">Students capture notebook photo</div>
                    </div>
                  </button>
                </div>
              </div>

              <FileUpload
                label="Worksheet or Practice Sheet (Optional)"
                hint={
                  editing?.attachment && removesSaved
                    ? `${editing.attachment.fileName} will be removed when you save.`
                    : editing?.attachment
                      ? `Currently ${editing.attachment.fileName}. Choose a file only to replace it.`
                      : 'Attach a question paper, reading sheet, or exercise diagram (PDF, JPG, PNG up to 5 MB).'
                }
                file={attachment}
                current={
                  editing?.attachment && !removesSaved
                    ? {
                        name: editing.attachment.fileName,
                        size: editing.attachment.sizeBytes,
                        path: homeworkApi.worksheetPath(editing.uuid),
                      }
                    : null
                }
                onRemoveCurrent={() => setRemovesSaved(true)}
                onSelect={setAttachment}
                disabled={isSaving}
              />
            </div>
          ) : (
            /* ================= COLLEGE ASSIGNMENT FORM ================= */
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Assignment / Project Title"
                  placeholder="e.g. Lab Project 2: Binary Search Tree Implementation"
                  required
                  maxLength={150}
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                />

                <DatePicker
                  label="Submission Cutoff Date"
                  required
                  min={today}
                  value={form.dueDate}
                  onChange={(event) => setForm({ ...form, dueDate: event.target.value })}
                />
              </div>

              <Textarea
                label="Problem Statement & Project Guidelines"
                rows={3}
                required
                maxLength={2000}
                placeholder="Implement BST insert, delete, and traversal methods in C++/Java..."
                value={form.instructions}
                onChange={(event) => setForm({ ...form, instructions: event.target.value })}
              />

              {maxMarksField}

              {/* Accepted Digital Submission Formats */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-ink-2">Accepted Digital Submission Format</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setCollegeSubmissionType('pdf')}
                    className={`rounded-lg border py-2 px-3 text-center text-xs transition-all ${
                      collegeSubmissionType === 'pdf'
                        ? 'border-indigo-500 bg-indigo-500/10 text-indigo-400 font-bold'
                        : 'border-line bg-surface text-ink-2'
                    }`}
                  >
                    📄 PDF Document
                  </button>
                  <button
                    type="button"
                    onClick={() => setCollegeSubmissionType('code')}
                    className={`rounded-lg border py-2 px-3 text-center text-xs transition-all ${
                      collegeSubmissionType === 'code'
                        ? 'border-indigo-500 bg-indigo-500/10 text-indigo-400 font-bold'
                        : 'border-line bg-surface text-ink-2'
                    }`}
                  >
                    💻 Code / GitHub Repo URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setCollegeSubmissionType('zip')}
                    className={`rounded-lg border py-2 px-3 text-center text-xs transition-all ${
                      collegeSubmissionType === 'zip'
                        ? 'border-indigo-500 bg-indigo-500/10 text-indigo-400 font-bold'
                        : 'border-line bg-surface text-ink-2'
                    }`}
                  >
                    📦 ZIP Archive / Project
                  </button>
                </div>
              </div>

              <FileUpload
                label="Assignment Question Paper / Project Rubric"
                hint={
                  editing?.attachment && removesSaved
                    ? `${editing.attachment.fileName} will be removed when you save.`
                    : editing?.attachment
                      ? `Currently ${editing.attachment.fileName}. Choose a file only to replace it.`
                      : 'Upload project problem statement or template (PDF, ZIP, DOCX up to 10 MB).'
                }
                file={attachment}
                current={
                  editing?.attachment && !removesSaved
                    ? {
                        name: editing.attachment.fileName,
                        size: editing.attachment.sizeBytes,
                        path: homeworkApi.worksheetPath(editing.uuid),
                      }
                    : null
                }
                onRemoveCurrent={() => setRemovesSaved(true)}
                onSelect={setAttachment}
                disabled={isSaving}
              />
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-2.5 border-t border-line pt-4">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={isSaving}
              onClick={() => {
                resetForm();
                setIsFormOpen(false);
              }}
            >
              Cancel
            </Button>
            {/* A draft can be saved again as a draft; published work cannot go back. */}
            {(!editing || editing.publication === 'DRAFT') && (
              <Button
                type="submit"
                name="publication"
                value="DRAFT"
                variant="secondary"
                size="sm"
                disabled={isSaving && savingAs !== 'DRAFT'}
                isLoading={savingAs === 'DRAFT'}
                leftIcon={<FilePen className="h-4 w-4" />}
              >
                {editing ? 'Save Draft' : 'Save as Draft'}
              </Button>
            )}
            <Button
              type="submit"
              name="publication"
              value="PUBLISHED"
              variant="primary"
              size="sm"
              disabled={isSaving && savingAs !== 'PUBLISHED'}
              isLoading={savingAs === 'PUBLISHED'}
              leftIcon={<Send className="h-4 w-4" />}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
            >
              {!editing ? copy.submit : editing.publication === 'DRAFT' ? 'Publish' : 'Save Changes'}
            </Button>
          </div>
        </form>
      )}

      {/* ================= 3. TWO-COLUMN MAIN SECTION ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* ================= LEFT SECTION (8-COLS): Tabs, Search, List & Pagination ================= */}
        <div className="lg:col-span-8 flex flex-col gap-4 min-w-0">
          {/* Tabs & Search Filter Header */}
          <div className="card bg-surface border border-line rounded-[16px] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 shadow-2xs">
            {/* Status Tabs */}
            <div className="flex items-center gap-4 sm:gap-6 overflow-x-auto no-scrollbar border-b sm:border-b-0 border-line pb-2 sm:pb-0 scroll-smooth">
              {[
                { key: 'all', label: copy.allTab },
                { key: 'open', label: 'Open' },
                { key: 'completed', label: 'Completed' },
                { key: 'draft', label: 'Drafts' },
                { key: 'deleted', label: 'Deleted' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={(e) => {
                    updateFilters({ view: tab.key as TabKey });
                    e.currentTarget.scrollIntoView({
                      behavior: 'smooth',
                      inline: 'center',
                      block: 'nearest',
                    });
                  }}
                  className={cn(
                    'text-[13px] font-bold transition-all relative py-1 cursor-pointer whitespace-nowrap',
                    activeTab === tab.key
                      ? 'text-indigo-400'
                      : 'text-ink-3 hover:text-ink',
                  )}
                >
                  {tab.label}
                  {activeTab === tab.key && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 rounded-full" />
                  )}
                </button>
              ))}
            </div>

            {/* Date Filter */}
            <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
              <div className="w-36 sm:w-40 shrink-0 ">
                <DatePicker
                  hideLabel
                  label="Filter by date"
                  size="sm"
                  placeholder="Select date"
                  value={onDate}
                  onChange={(e) => {
                    updateFilters({ date: e.target.value });
                  }}
                  // isClearable
                  className="h-9 w-full border border-line border-2 text-sm font-semibold rounded-field"
                />
              </div>

              <button
                type="button"
                disabled={!onDate}
                onClick={() => {
                  if (!onDate) return;
                  updateFilters({ date: '' });
                }}
                className={cn(
                  'inline-flex items-center justify-center gap-1 h-9 px-2 text-xs font-semibold rounded-field transition-colors shrink-0',
                  onDate
                    ? 'text-rose-500 bg-rose-500/10 border border-rose-500/40 hover:text-rose-600 hover:bg-rose-500/20 cursor-pointer'
                    : 'text-ink-3 opacity-40 cursor-not-allowed',
                )}
                title={onDate ? 'Clear date filter' : 'No filter to clear'}
              >
                <X className="h-3.5 w-3.5" />
                <span>Clear</span>
              </button>
            </div>
          </div>

          {/* List of Homework Cards */}
          {listLoading && (
            <div className="card p-5 bg-surface border border-line rounded-[16px]">
              <SkeletonLines count={6} />
            </div>
          )}

          {!listLoading && listError && (
            <div className="card flex flex-col items-start gap-3 p-5 bg-surface border border-line rounded-[16px]">
              <Alert tone="critical" title={copy.loadError}>
                {listError.message}
              </Alert>
              <Button variant="secondary" size="sm" onClick={listRefetch}>
                Try again
              </Button>
            </div>
          )}

          {!listLoading && !listError && paginatedHomework.length === 0 && (
            <div className="card p-8 bg-surface border border-line rounded-[16px]">
              <EmptyState
                icon={<FileText className="h-5 w-5" />}
                title={
                  searchQuery || onDate
                    ? 'No matching homework found'
                    : isDeletedTab
                      ? 'Nothing withdrawn'
                      : copy.emptyTitle
                }
                description={
                  searchQuery || onDate
                    ? 'Try adjusting your search query or clear the date filter.'
                    : isDeletedTab
                      ? 'Homework you withdraw from this class is listed here.'
                      : copy.emptyDescription
                }
              />
            </div>
          )}

          {!listLoading && !listError && paginatedHomework.length > 0 && (
            <HomeworkTable
              homework={paginatedHomework}
              today={today}
              defaultSubject={currentClass.subject}
              editMenuLabel={copy.editMenu}
              onReview={(item) => navigate(`/teacher/homework/${item.uuid}/review`)}
              onStartEditing={startEditing}
              onPublishDraft={(item) => void publishDraft(item)}
              onDownloadWorksheet={(item) => void openWorksheet(item)}
              onRemove={setRemoving}
            />
          )}

          {/* --- BOTTOM PAGINATION BAR ----------------------------------------- */}
          <div className="card bg-surface border border-line rounded-[16px] px-5 py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs shadow-2xs">
            <span className="text-ink-3 font-medium">
              Showing {filteredHomework.length === 0 ? 0 : (page - 1) * pageSize + 1} to{' '}
              {Math.min(page * pageSize, filteredHomework.length)} of {filteredHomework.length} {copy.countNoun}
            </span>

            <div className="flex items-center gap-4">
              <RowsPerPageSelect
                value={pageSize}
                options={PAGE_SIZES}
                onChange={(rows) => updateFilters({ limit: rows })}
              />

              {/* Page buttons */}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setFilters({ page: Math.max(page - 1, 1) })}
                  className="grid h-7 w-7 place-items-center rounded-[6px] border border-line bg-surface-2 text-ink hover:bg-surface-3 disabled:opacity-40 disabled:pointer-events-none transition-colors cursor-pointer"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setFilters({ page: p })}
                    className={cn(
                      'grid h-7 w-7 place-items-center rounded-[6px] text-xs font-bold transition-colors cursor-pointer',
                      p === page
                        ? 'bg-emerald-600 text-white shadow-xs'
                        : 'border border-line bg-surface-2 text-ink hover:bg-surface-3',
                    )}
                  >
                    {p}
                  </button>
                ))}

                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setFilters({ page: Math.min(page + 1, totalPages) })}
                  className="grid h-7 w-7 place-items-center rounded-[6px] border border-line bg-surface-2 text-ink hover:bg-surface-3 disabled:opacity-40 disabled:pointer-events-none transition-colors cursor-pointer"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ================= RIGHT SECTION (4-COLS): Calendar, Overview, Quick Actions, Help ================= */}
        <div className="lg:col-span-4 flex flex-col gap-5">
          {/* 2. HOMEWORK OVERVIEW WIDGET (total across the top, open and completed beneath) */}
          <div className="card bg-surface border border-line rounded-[16px] p-5 shadow-2xs flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-ink tracking-tight">{copy.overview}</h3>
                {/* The tiles count the list as filtered: say for which day. */}
                {onDate && (
                  <p className="text-xs text-ink-3 font-medium mt-0.5">
                    Set or due on {formatInstituteDate(onDate)}
                  </p>
                )}
              </div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-pill bg-surface-2 text-ink-3 border border-line text-xs font-semibold">
                <Users className="h-3 w-3 text-indigo-400" />
                <span className="text-ink1 text-sm">Total no. of students :<strong className="text-ink font-bold"> {currentClass.totalStudents}</strong></span>
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-3.5">
              {/* Total Card — the full row, so the three tiles fill the grid */}
              <div className="col-span-2 p-3.5 rounded-[12px] bg-surface-2/70 border border-line/80 flex items-center gap-3 shadow-2xs">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-purple-500/15 text-purple-400 border border-purple-500/25">
                  <Layers className="h-5 w-5" />
                </span>
                <div>
                  <span className="text-2xs font-bold text-ink-3 uppercase tracking-wider block">Total</span>
                  <p className="text-2xl font-black text-ink mt-0.5 leading-tight font-mono">
                    {String(overviewStats.total).padStart(2, '0')}
                  </p>
                </div>
              </div>

              {/* Posted Card */}
              <div className="p-3.5 rounded-[12px] bg-surface-2/70 border border-line/80 flex items-center gap-3 shadow-2xs">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-blue-500/15 text-blue-400 border border-blue-500/25">
                  <Send className="h-5 w-5" />
                </span>
                <div>
                  <span className="text-2xs font-bold text-ink-3 uppercase tracking-wider block">Open</span>
                  <p className="text-2xl font-black text-blue-400 mt-0.5 leading-tight font-mono">
                    {String(overviewStats.open).padStart(2, '0')}
                  </p>
                </div>
              </div>

              {/* Completed Card */}
              <div className="p-3.5 rounded-[12px] bg-surface-2/70 border border-line/80 flex items-center gap-3 shadow-2xs">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                  <CheckCircle2 className="h-5 w-5" />
                </span>
                <div>
                  <span className="text-2xs font-bold text-ink-3 uppercase tracking-wider block">Completed</span>
                  <p className="text-2xl font-black text-emerald-400 mt-0.5 leading-tight font-mono">
                    {String(overviewStats.completed).padStart(2, '0')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 3. QUICK ACTIONS WIDGET */}
          <div className="card bg-surface border border-line rounded-[16px] p-5 shadow-2xs flex flex-col gap-4">
            <h3 className="text-base font-bold text-ink tracking-tight">Quick Actions</h3>

            <div className="flex flex-col gap-2.5">
              {/* Create Homework */}
              <button
                type="button"
                onClick={() => {
                  setIsFormOpen(true);
                  setTimeout(() => {
                    const headerEl = document.getElementById('homework-page-header');
                    if (headerEl) {
                      headerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }, 60);
                }}
                className="group p-3 sm:px-3.5 rounded-[12px] bg-surface-2/70 hover:bg-surface-2 border border-line/80 hover:border-indigo-500/40 transition-all flex items-center justify-between text-left cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[8px] bg-purple-500/15 text-purple-400 border border-purple-500/25">
                    <Plus className="h-4 w-4" />
                  </span>
                  <div>
                    <h4 className="text-sm font-bold text-ink group-hover:text-indigo-400 transition-colors">
                      {copy.quickCreate}
                    </h4>
                    <p className="text-xs text-ink-3 font-medium">{copy.quickCreateHint}</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-ink-3 group-hover:text-ink transition-colors" />
              </button>

              {/* View Submissions */}
              <button
                type="button"
                disabled={!latestPublished}
                onClick={() => {
                  if (latestPublished) navigate(`/teacher/homework/${latestPublished.uuid}/review`);
                }}
                className="group p-3 sm:px-3.5 rounded-[12px] bg-surface-2/70 hover:bg-surface-2 border border-line/80 hover:border-indigo-500/40 transition-all flex items-center justify-between text-left cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
              >
                <div className="flex items-center gap-3">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[8px] bg-amber-500/15 text-amber-400 border border-purple-500/25">
                    <FileCheck className="h-4 w-4" />
                  </span>
                  <div>
                    <h4 className="text-sm font-bold text-ink group-hover:text-indigo-400 transition-colors">
                      View Submissions
                    </h4>
                    <p className="text-xs text-ink-3 font-medium">
                      {latestPublished
                        ? `Latest set: ${latestPublished.title}`
                        : 'Nothing published yet'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-ink-3 group-hover:text-ink transition-colors" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Withdraw Confirm Dialog */}
      <ConfirmDialog
        isOpen={Boolean(removing)}
        tone="danger"
        title={
          removing?.publication === 'DRAFT'
            ? `Delete the draft “${removing.title}”?`
            : `Withdraw “${removing?.title ?? copy.removeSubject}”?`
        }
        description={
          removing?.publication === 'DRAFT'
            ? 'The students never saw it. It moves to the Deleted tab.'
            : 'Nothing is deleted. It stops appearing for the students, and any marks already given are kept.'
        }
        confirmLabel={copy.removeConfirm}
        isLoading={isRemoving}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
